import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export const useAutoLogout = () => {
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const [timeoutMinutes, setTimeoutMinutes] = useState(30); // Default 30 mins
    const lastActivity = useRef(Date.now());
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Load timeout setting from Firestore
    useEffect(() => {
        const loadTimeoutSetting = async () => {
            try {
                const db = getFirestore();
                const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    if (data.sessionTimeout && typeof data.sessionTimeout === 'number') {
                        setTimeoutMinutes(data.sessionTimeout);
                        console.log(`⏱️ Session timeout set to ${data.sessionTimeout} minutes`);
                    }
                }
            } catch (error) {
                console.error('Failed to load session timeout setting:', error);
            }
        };

        if (currentUser) {
            loadTimeoutSetting();
        }
    }, [currentUser]);

    const updateActivity = useCallback(() => {
        lastActivity.current = Date.now();
    }, []);

    const checkActivity = useCallback(() => {
        if (!currentUser) return;

        const now = Date.now();
        const timeSinceLastActivity = now - lastActivity.current;
        const timeoutMs = timeoutMinutes * 60 * 1000;

        if (timeSinceLastActivity > timeoutMs) {
            console.log('💤 Session timed out due to inactivity. Logging out...');
            logout();
            // Always redirect to regular login, not admin login
            navigate('/login');
        }
    }, [currentUser, logout, navigate, timeoutMinutes]);

    useEffect(() => {
        if (!currentUser) return;

        // Events to track activity
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

        // Add listeners
        events.forEach(event => {
            window.addEventListener(event, updateActivity);
        });

        // Initial update
        updateActivity();

        // Check every minute
        timerRef.current = setInterval(checkActivity, 60 * 1000);

        return () => {
            events.forEach(event => {
                window.removeEventListener(event, updateActivity);
            });
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [currentUser, updateActivity, checkActivity]);

    return;
};
